<template>
    <v-card class="scrollDiv" rounded="lg" :elevation="5">
        <div v-if="Object.keys(searchResults).length === 0 && loading" class="text-center pa-4">
            <div class="text-body-medium text-medium-emphasis mb-1">Searching: {{ searchQuery }}</div>
            <v-progress-linear color="info" indeterminate rounded />
        </div>

        <div v-else-if="Object.keys(searchResults).length === 0" class="text-center pa-4">
            <v-icon color="error" class="mb-1">$errorOutline</v-icon>
            <div class="text-body-medium text-medium-emphasis">No results found for: {{ searchQuery }}</div>
        </div>

        <v-list v-else lines="two" class="py-1">
            <v-list-item class="borderBottom" v-for="(result, index) in searchResults" :key="index"
                v-bind:id="'item-' + index" :to="goToView(result.type, result.id)" :active="currentItem === index" link>
                <v-list-item-title class="d-flex align-center">
                    <v-chip size="x-small" variant="outlined" class="mr-2 text-capitalize chipFixed"
                        :color="getColorForType(result.type)">
                        <span class="text-primary">{{ result.type }}</span>
                    </v-chip>
                    <span class="text-body-medium font-weight-medium text-truncate">{{ result.name }}</span>
                </v-list-item-title>

                <!-- Campaign -->
                <v-list-item-subtitle v-if="result.type == 'campaign'" class="subtitleAligned text-body-small">
                    <span class="text-capitalize">{{ getStatusIcon(result).label }}</span>
                    <span class="text-disabled"> |
                        {{ formatDateShort(result.launch_date) }} &ndash; {{ formatDateShort(result.end_date) }}
                    </span>
                </v-list-item-subtitle>

                <!-- Creative -->
                <v-list-item-subtitle v-else-if="result.type == 'creative'" class="subtitleAligned text-body-small">
                    <span class="text-capitalize">{{ getStatusIcon(result).label }}</span>
                    <span class="text-disabled"> | {{ result.width }}&times;{{ result.height }}
                        <span v-if="result.language"> | {{ getLanguageName(result.language) }}</span>
                    </span>
                </v-list-item-subtitle>

                <!-- Profile -->
                <v-list-item-subtitle v-else-if="result.type == 'profile'" class="subtitleAligned text-body-small">
                    <span class="text-capitalize">{{ getStatusIcon(result).label }}</span>
                    <span class="text-disabled"> | Updated: {{ formatDateShort(result.updated_at) }}</span>
                </v-list-item-subtitle>
            </v-list-item>
        </v-list>
    </v-card>
</template>

<script>
import debounce from "lodash/debounce";
import eventBus from "../eventBus";
import { formatDateShort } from "@/utils/formatters";
import { getStatusIcon } from "@/stores/helpers";
import { getLanguageName } from "@/constants/languages";

export default {
    props: {
        searchQuery: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            currentItem: 0,
            searchResults: {},
            loading: true,
        };
    },
    methods: {
        formatDateShort,
        getStatusIcon,
        getLanguageName,

        goToView(viewName, id) {
            return {
                name: `${viewName}.view`,
                params: {
                    id: id,
                },
            };
        },
        readResults(payload) {
            this.loading = true;
            return new Promise((resolve, reject) => {
                axios
                    .get(`/v1/search/${payload}`)
                    .then(function (response) {
                        resolve(response);
                    })
                    .catch(function (error) {
                        reject(error);
                    });
            });
        },
        updateResults() {
            this.currentItem = 0;
            let queryUsed = btoa(this.searchQuery);

            this.readResults(queryUsed)
                .then((response) => {
                    // Check if after getting the response the string in the search component is the same
                    let currentQuery = btoa(this.searchQuery);

                    if (currentQuery !== queryUsed) {
                        return;
                    }

                    this.searchResults = response.data?.length
                        ? response.data
                        : {};

                    this.loading = false;
                })
                .catch(() => {
                    this.searchResults = {};
                    this.loading = false;
                })
                .finally(() => { });
        },
        getColorForType(type) {
            switch (type) {
                case "campaign":
                    return "fluxYellow";

                case "creative":
                    return "fluxOrange";

                case "profile":
                    return "fluxPurple";

                default:
                    return ";";
            }
        },
        nextItem() {
            const resultsLength = Object.keys(this.searchResults).length - 1;

            const pressedDown = event.keyCode == 38 && this.currentItem > 0;
            const pressedUp =
                event.keyCode == 40 && this.currentItem < resultsLength;
            const pressedEnter = event.keyCode == 13;

            if (pressedDown) {
                this.currentItem--;
                document.getElementById(`item-${this.currentItem}`)?.scrollIntoView({ block: "nearest" });
            } else if (pressedUp) {
                this.currentItem++;
                document.getElementById(`item-${this.currentItem}`)?.scrollIntoView({ block: "nearest" });
            } else if (
                pressedEnter &&
                this.searchResults.hasOwnProperty(this.currentItem)
            ) {
                let selectedObj = this.searchResults[this.currentItem];
                this.$router.push(this.goToView(selectedObj.type, selectedObj.id));
                eventBus.emit("CLEAR_SEARCH_RESULTS", null);
            }
        },
    },
    watch: {
        searchQuery: debounce(function () {
            this.updateResults();
        }, 400),
        $route() {
            eventBus.emit("CLEAR_SEARCH_RESULTS", null);
        },
    },
    created() {
        this.updateResults();
        window.addEventListener("keyup", this.nextItem);
    },
    unmounted() {
        window.removeEventListener("keyup", this.nextItem);
    },
};
</script>

<style scoped>
.scrollDiv {
    max-height: 75vh;
    overflow: auto;
}

.borderBottom {
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    padding-left: 8px !important;
}

.chipFixed {
    min-width: 72px;
    margin-top: 5px;
    justify-content: center;
}

.subtitleAligned {
    margin-left: 80px;
}

.dataFeedLink {
    color: rgb(var(--v-theme-info)) !important;
    text-decoration: none;
    align-items: center;
    margin-right: 6px;
}

.dataFeedLink:hover {
    text-decoration: underline;
}
</style>
